import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import CreateProposalModal from './CreateProposalModal';
import AddTripModal from './AddTripModal';
import AddClientModal from './AddClientModal';
import ProposalDetails from './ProposalDetails';
import TripDetails from './TripDetails';
import ClientDetails from './ClientDetails';
import API_BASE_URL from '../config';
import './AdminIndex.css';

const AdminIndex = () => {
  const [stats, setStats] = useState({
    totalProposals: 0,
    totalItineraries: 0,
    totalClients: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [clients, setClients] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    
    // Check for view parameter in URL
    const viewParam = searchParams.get('view');
    if (viewParam && ['proposals', 'itineraries', 'clients', 'adminUsers'].includes(viewParam)) {
      setActiveView(viewParam);
    }
    
    fetchDashboardData();
  }, [user, navigate, searchParams]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [proposalsResponse, itinerariesResponse, clientsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/proposals`),
        axios.get(`${API_BASE_URL}/api/trips`),
        axios.get(`${API_BASE_URL}/api/clients`),
        axios.get(`${API_BASE_URL}/api/users`)
      ]);

      const proposalsData = proposalsResponse.data.data || [];
      const itinerariesData = itinerariesResponse.data.data || [];
      const clientsData = clientsResponse.data.data || [];
      const usersData = usersResponse.data.data || [];

      // Filter admin users
      const adminUsersData = usersData.filter(user => user.role === 'admin');

      // Store data in state
      setProposals(proposalsData);
      setItineraries(itinerariesData);
      setClients(clientsData);
      setAdminUsers(adminUsersData);

      // Calculate stats
      setStats({
        totalProposals: proposalsData.length,
        totalItineraries: itinerariesData.length,
        totalClients: clientsData.length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter functions
  const getFilteredProposals = () => {
    let filtered = proposals;
    if (searchTerm) {
      filtered = filtered.filter(proposal => 
        proposal.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.clientId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.clientId?.lastName1?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(proposal => proposal.status === statusFilter);
    }
    return filtered;
  };

  const getFilteredItineraries = () => {
    let filtered = itineraries;
    if (searchTerm) {
      filtered = filtered.filter(itinerary => 
        itinerary.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        itinerary.traveler?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(itinerary => itinerary.status === statusFilter);
    }
    return filtered;
  };

  const getFilteredClients = () => {
    let filtered = clients;
    if (searchTerm) {
      filtered = filtered.filter(client => 
        client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.lastName1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  };

  const getFilteredAdminUsers = () => {
    let filtered = adminUsers;
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  };

  const getStatusOptions = () => {
    switch (activeView) {
      case 'proposals':
        return ['draft', 'published', 'archived'];
      case 'itineraries':
        return ['Upcoming', 'Ongoing', 'Completed'];
      default:
        return [];
    }
  };

  const getHeaderTitle = () => {
    switch (activeView) {
      case 'proposals':
        return 'Proposals';
      case 'itineraries':
        return 'Itineraries';
      case 'clients':
        return 'Clients';
      case 'users':
        return 'Admin Users';
      default:
        return 'Dashboard';
    }
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
  };

  // Specific update handlers for each data type
  const handleProposalUpdate = async (newProposal = null, refreshItineraries = false) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/proposals`);
      const proposalsData = response.data.data || [];
      setProposals(proposalsData);
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        totalProposals: proposalsData.length
      }));

      // Also refresh itineraries if requested (e.g., after converting proposal to itinerary)
      if (refreshItineraries) {
        await handleItineraryUpdate();
      }
    } catch (error) {
      console.error('Error updating proposals:', error);
    }
  };

  const handleItineraryUpdate = async (newItinerary = null) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trips`);
      const itinerariesData = response.data.data || [];
      setItineraries(itinerariesData);
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        totalItineraries: itinerariesData.length
      }));
    } catch (error) {
      console.error('Error updating itineraries:', error);
    }
  };

  const handleClientUpdate = async (newClient = null) => {
    try {
      // If newClient is provided, save it to the database first
      if (newClient) {
        // Step 1: Create Firebase user with email and password
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          newClient.email, 
          newClient.password
        );
        
        if (!userCredential.user) {
          throw new Error('Failed to create Firebase user');
        }
        
        // Step 2: Create user record in database (without password)
        const clientData = {
          email: newClient.email,
          name: newClient.name,
          midName: newClient.midName,
          lastName1: newClient.lastName1,
          lastName2: newClient.lastName2,
          dob: newClient.dob ? new Date(newClient.dob).toISOString().split('T')[0] : null
        };
        
        await axios.post(`${API_BASE_URL}/api/clients/with-user`, clientData);
      }
      
      // Then fetch the updated list of clients
      const response = await axios.get(`${API_BASE_URL}/api/clients`);
      const clientsData = response.data.data || [];
      setClients(clientsData);
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        totalClients: clientsData.length
      }));
    } catch (error) {
      console.error('Error updating clients:', error);
      const errorMessage = error.code === 'auth/email-already-in-use' 
        ? 'This email is already registered' 
        : (error.response?.data?.error || error.message || 'Failed to save client');
      alert('Failed to save client: ' + errorMessage);
    }
  };

  const handleItineraryDetailsUpdate = (updatedItinerary) => {
    // When an itinerary is updated, refresh the itineraries data to ensure all related itineraries are updated
    // This is especially important for showItinerary changes that affect multiple itineraries
    const refreshItineraries = async () => {
      try {
        const itinerariesResponse = await axios.get(`${API_BASE_URL}/api/trips`);
        const itinerariesData = itinerariesResponse.data.data || [];
        setItineraries(itinerariesData);
      } catch (error) {
        console.error('Error refreshing itineraries after update:', error);
        // Fallback to updating just the single itinerary if refresh fails
        setItineraries(prevItineraries => 
          prevItineraries.map(itinerary => 
            itinerary.id === updatedItinerary.id ? updatedItinerary : itinerary
          )
        );
      }
    };
    
    refreshItineraries();
  };

  const handleItemClick = (item, type) => {
    console.log('🔍 Item clicked:', item);
    console.log('🔍 Item type:', type);
    console.log('🔍 Item _id:', item._id);
    setSelectedItem({ ...item, type });
    setViewMode('details');
  };


  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>{viewMode === 'details' ? 
              (selectedItem?.type === 'proposal' ? 'Proposal Details' : 
               selectedItem?.type === 'itinerary' ? 'Itinerary Details' : 
               selectedItem?.type === 'client' ? 'Client Details' : 'Details') : 
              getHeaderTitle()}</h1>
          </div>
          <div className="header-actions">
            <span className="welcome-text">Welcome, {user?.name && user?.lastName1 ? `${user.name} ${user.lastName1}` : user?.name || 'Admin'}!</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="sidebar-header">
            <h2>Menu</h2>
          </div>
          <div className="sidebar-menu">
            <div className={`menu-item ${activeView === 'proposals' ? 'active' : ''}`} onClick={() => {
              if (activeView === 'proposals') {
                // If already on proposals, refresh the data
                handleProposalUpdate();
              } else {
                setActiveView('proposals');
              }
              setViewMode('list');
              setSelectedItem(null);
            }}>
              <span className="menu-title">Proposals</span>
            </div>
            
            <div className={`menu-item ${activeView === 'itineraries' ? 'active' : ''}`} onClick={() => {
              if (activeView === 'itineraries') {
                // If already on itineraries, refresh the data
                handleItineraryUpdate();
              } else {
                setActiveView('itineraries');
              }
              setViewMode('list');
              setSelectedItem(null);
            }}>
              <span className="menu-title">Itineraries</span>
            </div>
            
            <div className={`menu-item ${activeView === 'clients' ? 'active' : ''}`} onClick={() => {
              if (activeView === 'clients') {
                // If already on clients, refresh the data
                handleClientUpdate();
              } else {
                setActiveView('clients');
              }
              setViewMode('list');
              setSelectedItem(null);
            }}>
              <span className="menu-title">Clients</span>
            </div>
            
            <div className="settings-menu">
              <div className="settings-header" onClick={() => setSettingsOpen(!settingsOpen)}>
                <span className="settings-title">Settings</span>
                <span className="settings-toggle">{settingsOpen ? '−' : '+'}</span>
              </div>
              {settingsOpen && (
                <div className="settings-items">
                  <div className={`menu-item ${activeView === 'users' ? 'active' : ''}`} onClick={() => {
                    setActiveView('users');
                    setViewMode('list');
                    setSelectedItem(null);
                  }}>
                    <span className="menu-title">Users</span>
                  </div>
                  <div className="menu-item logout-item" onClick={handleLogout}>
                    <span className="menu-title">Logout</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="sidebar-footer">
          </div>
        </div>
        
        {/* Content Area */}
        <div className="content-area">
          {viewMode === 'details' && selectedItem ? (
            <>
              {selectedItem.type === 'proposal' && (
                <ProposalDetails proposalId={selectedItem._id} isDashboard={true} onProposalUpdate={handleProposalUpdate} />
              )}
              {selectedItem.type === 'itinerary' && (
                <TripDetails tripId={selectedItem.id} isDashboard={true} onTripUpdate={handleItineraryDetailsUpdate} />
              )}
              {selectedItem.type === 'client' && (
                <ClientDetails clientId={selectedItem._id} isDashboard={true} />
              )}
            </>
          ) : (
            activeView && (
              <div className="content-section">
                {activeView === 'proposals' && (
                  <div className="list-container">
                    <div className="list-filters">
                      <div className="filter-group">
                        <input
                          type="text"
                          placeholder="Search proposals..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-input"
                        />
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="status-select"
                        >
                          <option value="">All Status</option>
                          {getStatusOptions().map(status => (
                            <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                          ))}
                        </select>
                        <button className="add-button" onClick={handleAddClick}>
                          +
                        </button>
                      </div>
                    </div>
                    <div className="list-content">
                      {getFilteredProposals().length > 0 ? (
                        <div className="list-items">
                          {getFilteredProposals().map((proposal) => (
                            <div key={proposal._id || proposal.id} className="list-item" onClick={() => handleItemClick(proposal, 'proposal')}>
                              <div className="item-main">
                                <h4 className="item-title">{proposal.title}</h4>
                                <div className="item-meta">
                                  <span className="client-name">
                                    {proposal.clientId ? 
                                      `${proposal.clientId.name} ${proposal.clientId.lastName1}` : 
                                      'Unknown Client'
                                    }
                                  </span>
    
                                </div>
                              </div>
                              <div className="item-status">
                                <span className={`status-badge status-${proposal.status}`}>
                                  {proposal.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-state">
                          <p>No proposals found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeView === 'itineraries' && (
                  <div className="list-container">
                    <div className="list-filters">
                      <div className="filter-group">
                        <input
                          type="text"
                          placeholder="Search itineraries..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-input"
                        />
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="status-select"
                        >
                          <option value="">All Status</option>
                          {getStatusOptions().map(status => (
                            <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                          ))}
                        </select>
                        <button className="add-button" onClick={handleAddClick}>
                          +
                        </button>
                      </div>
                    </div>
                    <div className="list-content">
                      {getFilteredItineraries().length > 0 ? (
                        <div className="list-items">
                          {getFilteredItineraries().map((itinerary) => (
                            <div key={itinerary.id || itinerary._id} className="list-item" onClick={() => handleItemClick(itinerary, 'itinerary')}>
                              <div className="item-main">
                                <h4 className="item-title">{itinerary.title}</h4>
                                <div className="item-meta">
                                  <span className="client-name">
                                    {itinerary.traveler || 'Unknown Client'}
                                  </span>
                                </div>
                              </div>
                              <div className="item-status">
                                <span className={`status-badge status-${itinerary.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                                  {itinerary.status}
                                </span>
                                <span className={`status-badge status-itinerary ${itinerary.showItinerary ? 'active' : 'inactive'}`}>
                                  {itinerary.showItinerary ? 'Published' : 'Draft'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-state">
                          <p>No itineraries found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeView === 'clients' && (
                  <div className="list-container">
                    <div className="list-filters">
                      <div className="filter-group">
                        <input
                          type="text"
                          placeholder="Search clients..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-input"
                        />
                        <button className="add-button" onClick={handleAddClick}>
                          +
                        </button>
                      </div>
                    </div>
                    <div className="list-content">
                      {getFilteredClients().length > 0 ? (
                        <div className="list-items">
                          {getFilteredClients().map((client) => (
                            <div key={client._id || client.id} className="list-item" onClick={() => handleItemClick(client, 'client')}>
                              <div className="item-main">
                                <h4 className="item-title">{client.name} {client.lastName1}</h4>
                                <div className="item-meta">
                                  <span className="client-email">{client.email}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-state">
                          <p>No clients found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeView === 'users' && (
                  <div className="list-container">
                    <div className="list-filters">
                      <div className="filter-group">
                        <input
                          type="text"
                          placeholder="Search admin users..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="search-input"
                        />
                      </div>
                    </div>
                    <div className="list-content">
                      {getFilteredAdminUsers().length > 0 ? (
                        <div className="list-items">
                          {getFilteredAdminUsers().map((adminUser) => (
                            <div key={adminUser._id || adminUser.id} className="list-item">
                              <div className="item-main">
                                <h4 className="item-title">{adminUser.name && adminUser.lastName1 ? `${adminUser.name} ${adminUser.lastName1}` : adminUser.name || 'No Name'}</h4>
                                <div className="item-meta">
                                  <span className="user-email">{adminUser.email}</span>                            
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-state">
                          <p>No admin users found</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddModal && activeView === 'proposals' && (
        <CreateProposalModal 
          onSave={(proposal) => {
            handleProposalUpdate(proposal);
            handleModalClose();
          }} 
          onClose={handleModalClose} 
        />
      )}
      {showAddModal && activeView === 'itineraries' && (
        <AddTripModal 
          onSave={(trip) => {
            handleItineraryUpdate(trip);
            handleModalClose();
          }} 
          onClose={handleModalClose} 
        />
      )}
      {showAddModal && activeView === 'clients' && (
        <AddClientModal 
          onSave={(client) => {
            handleClientUpdate(client);
            handleModalClose();
          }} 
          onClose={handleModalClose} 
        />
      )}
    </div>
  );
};

export default AdminIndex;
